import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import {
    availableCoins,
    MarketMostActiveQueries,
    MarketMostActiveSupportedOrders,
    MarketMostAtiveSupportedMarkets,
} from '../constants/lookups';
import {
    CryptoCoin,
    EtfBreakdownEntry,
    MiddlewareConfig,
    MostActive,
    MostActiveWithExtraInfo,
    OptionChain,
    OptionChainDates,
    Quote,
    Sdks,
    Shortability,
    Socials,
    TiprankOverviewWithExtraInfo,
} from '../types';
import { formatOptionChain, formatQuote, parseCryptoSymbol } from '../utils';
import { IQuoteApi } from './interfaces/IQuoteApi';
import { MiddlewareInjectToken, ApiInjectToken } from '../constants/tokens';
import { ITipranksApi } from './interfaces';

@injectable()
export class QuoteApi extends BaseApi implements IQuoteApi {
    constructor(
        @inject(MiddlewareInjectToken.Sdks) sdks: Sdks,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.TipranksApi) protected tipranksApi: ITipranksApi,
    ) {
        super(sdks, config);
    }
    async getTopTenAssets(): Promise<Quote[]> {
        const response = await this.sdks.orbis.get<EtfBreakdownEntry[]>('quotes/top10', undefined, {
            urlQuery: 'MA',
            params: { market: 'BIG3', order: 'H' },
        });

        return response?.data.map((item) => item.quote);
    }

    getStablecoin(): Promise<CryptoCoin | null> {
        throw new Error('Not supported');
    }

    getAvailableCoin(): Promise<CryptoCoin[]> {
        return Promise.resolve<CryptoCoin[]>(Object.values(availableCoins));
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */

    getCoin(symbol: string): Promise<CryptoCoin | null> {
        const { base } = parseCryptoSymbol(symbol);
        return Promise.resolve(availableCoins[base] || null);
    }

    async searchQuotes(criteria: string): Promise<Quote[]> {
        const results = (
            await this.sdks.orbis.get('quotes/search', undefined, { params: { criteria, limit: 10, loadQuotes: true } })
        ).data as Quote[];

        return results.map(formatQuote);
    }

    async getQuotes(symbols: string[], crypto?: boolean): Promise<Quote[]> {
        const symbolsString = symbols.join(',');
        const response = await (crypto
            ? // @ts-ignore: TODO: url not in SDK
              this.sdks.orbis.get('quotes/crypto', undefined, { params: { symbols: symbolsString } })
            : this.sdks.orbis.get('quotes/equity', undefined, { params: { symbols: symbolsString } }));

        return response.data.map(formatQuote);
    }

    async getQuote(symbol: string, crypto?: boolean): Promise<Quote | null> {
        const quotes: Quote[] = (
            await (crypto
                ? // @ts-ignore: TODO: URL not in SDK
                  this.sdks.orbis.get('quotes/crypto', undefined, { params: { symbols: symbol } })
                : this.sdks.orbis.get('quotes/equity', undefined, { params: { symbols: symbol } }))
        ).data;
        const quote = quotes.find((quote) => quote?.symbol === symbol);
        return quote ? formatQuote(quote) : null;
    }

    async getOptionQuote(symbol: string): Promise<Quote | null> {
        const response = await this.sdks.orbis.get('quotes/option', undefined, {
            params: { symbols: symbol, greeks: true },
        });
        return response.data[0];
    }

    async getQuoteOptionChainDates(symbol: string): Promise<OptionChainDates> {
        const response = await this.sdks.orbis.get('quotes/option/chain/dates', undefined, { urlQuery: symbol });
        return response.data;
    }

    async getQuoteOptionChainQuotes(symbol: string, date: string): Promise<OptionChain> {
        const response = await this.sdks.orbis.get('quotes/option/chain', undefined, { urlQuery: `${symbol}/${date}` });
        return formatOptionChain(response.data);
    }

    async getMostActiveStocks(
        order: MarketMostActiveSupportedOrders,
        urlQuery: MarketMostActiveQueries,
        tipranks?: boolean,
    ): Promise<MostActiveWithExtraInfo[]> {
        const mostActiveResponse = await this.sdks.orbis.get<MostActive[]>('quotes/top10', undefined, {
            params: {
                market: MarketMostAtiveSupportedMarkets.Big3,
                order,
            },
            urlQuery,
        });
        let tipranksData: TiprankOverviewWithExtraInfo[] = [];
        if (tipranks) {
            tipranksData = await this.tipranksApi.getTipranksAssetsOverview(
                mostActiveResponse.data.map((item: MostActive) => item.quote.symbol),
            );
        }

        return mostActiveResponse.data.map((mostActiveItem: MostActive) => {
            return {
                ...mostActiveItem.quote,
                tipranks: tipranksData.find((item) => item.ticker === mostActiveItem.quote.symbol),
            };
        });
    }

    async getQuoteShortability(symbol: string): Promise<Shortability> {
        const response = await this.sdks.orbis.get('quotes/equity/shortability', undefined, { urlQuery: symbol });
        return response.data;
    }

    async getQuoteSocials(symbol: string): Promise<Socials> {
        const response = await this.sdks.middleware.get('logos/social', undefined, { urlQuery: symbol });
        return response.data;
    }

    /* eslint-enable @typescript-eslint/no-unused-vars */
}
